import type { ContextProbe } from '@fraudeai/schema';

/**
 * Проба, исполняемая в произвольном контексте: главном потоке, воркере,
 * service worker или iframe.
 *
 * ЖЁСТКОЕ ТРЕБОВАНИЕ: функция обязана быть полностью самодостаточной.
 * Её исходник берётся через `String(contextProbe)` и вставляется в Blob для
 * воркера — там нет ни импортов, ни замыканий этого модуля. Любая ссылка на
 * внешний идентификатор превратится в ReferenceError внутри воркера.
 * Поэтому здесь продублированы даже мелкие хелперы, включая хеш.
 *
 * Второе требование: все встроенные объекты берутся из `S` (то есть из
 * `scope`), а не из лексического окружения. Для воркера разницы нет, а вот для
 * iframe она принципиальна — код физически исполняется в родительском realm,
 * и обращение к глобальному `Math` вернуло бы `Math` родителя. Тогда фрейм
 * всегда совпадал бы с главным потоком, и проверка потеряла бы смысл.
 *
 * `scope` — глобальный объект целевого контекста (`self` в воркере,
 * `iframe.contentWindow` для фрейма).
 */
export function contextProbe(scope: any): ContextProbe {
  const S: any = scope;

  /**
   * Верхнее ли это окно.
   *
   * Хелпер обязан жить внутри contextProbe: исходник всей функции вставляется
   * в Worker через String(contextProbe), и внешнего замыкания там нет.
   */
  const topLevel = (): boolean => {
    try {
      return typeof S.window !== 'undefined' && S.window === S.window.top;
    } catch {
      // Обращение к родителю из другого источника бросает исключение — значит
      // родитель есть, и это не верхнее окно.
      return false;
    }
  };

  /** Локальный try/catch: любое из этих свойств может быть ловушкой. */
  const t = <T>(fn: () => T): T | null => {
    try {
      const v = fn();
      return v === undefined ? null : v;
    } catch {
      return null;
    }
  };

  /**
   * Приведение типов через t(), а не через String()/Number() напрямую.
   *
   * Иначе отсутствующее в контексте свойство превращается в строку
   * "undefined" или в NaN, и сравнение контекстов видит «расхождение» там,
   * где на самом деле сигнал просто недоступен: `navigator.vendor` и
   * `devicePixelRatio` в воркерах не существуют по спецификации.
   * Недоступность обязана давать null — только тогда её игнорирует diff.
   */
  const str = (fn: () => unknown): string | null => {
    const v = t(fn);
    return v === null || v === undefined ? null : String(v);
  };
  const num = (fn: () => unknown): number | null => {
    const v = t(fn);
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const bool = (fn: () => unknown): boolean | null => {
    const v = t(fn);
    return v === null || v === undefined ? null : Boolean(v);
  };

  /**
   * FNV-1a. Намеренно не murmur из util/hash: тот модуль сюда не втащить,
   * а для сравнения контекстов между собой достаточно любой детерминированной
   * функции — важно лишь, чтобы она была одна и та же во всех контекстах.
   */
  const fnv = (input: string | Uint8Array): string => {
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;
    let bytes: Uint8Array;
    if (typeof input === 'string') {
      if (S.TextEncoder) {
        bytes = new S.TextEncoder().encode(input);
      } else {
        const tmp: number[] = [];
        for (let i = 0; i < input.length; i++) tmp.push(input.charCodeAt(i) & 0xff);
        bytes = new Uint8Array(tmp);
      }
    } else {
      bytes = input;
    }
    for (let i = 0; i < bytes.length; i++) {
      h1 = Math.imul(h1 ^ bytes[i], 0x01000193) >>> 0;
      h2 = Math.imul(h2 ^ bytes[i], 0x85ebca6b) >>> 0;
    }
    return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
  };

  const nav = t(() => S.navigator) as any;

  /**
   * Canvas через OffscreenCanvas везде, где он есть: в воркере другого пути
   * нет, а сравнивать нужно одинаковым способом, иначе разница между
   * OffscreenCanvas и обычным canvas даст ложное срабатывание.
   */
  const drawCanvas = (): string | null =>
    t(() => {
      let ctx: any = null;
      if (S.OffscreenCanvas) {
        ctx = new S.OffscreenCanvas(200, 60).getContext('2d');
      } else if (S.document) {
        const c = S.document.createElement('canvas');
        c.width = 200;
        c.height = 60;
        ctx = c.getContext('2d');
      }
      if (!ctx) return null;

      ctx.textBaseline = 'top';
      ctx.font = '14px "Arial", sans-serif';
      ctx.fillStyle = '#f60';
      ctx.fillRect(0, 0, 60, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('fraudeAI ctx 😃', 2, 22);
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = '#2ff';
      ctx.beginPath();
      ctx.arc(140, 30, 25, 0, Math.PI * 2, true);
      ctx.fill();

      const data = ctx.getImageData(0, 0, 200, 60).data;
      return fnv(new Uint8Array(data.buffer.slice(0)));
    });

  const webgl = t(() => {
    let gl: any = null;
    if (S.OffscreenCanvas) {
      gl = new S.OffscreenCanvas(32, 32).getContext('webgl');
    } else if (S.document) {
      gl = S.document.createElement('canvas').getContext('webgl');
    }
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      renderer: ext
        ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
        : String(gl.getParameter(gl.RENDERER)),
      vendor: ext
        ? String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL))
        : String(gl.getParameter(gl.VENDOR)),
    };
  }) as { renderer: string; vendor: string } | null;

  // Тексты ошибок различаются между движками JS — и обязаны совпадать
  // между контекстами одного браузера.
  //
  // Сообщения сохраняются целиком, а не только их хеш: при расхождении
  // отчёт должен показать, какое именно сообщение разошлось. Иначе
  // пользователь видит два хеша и не может ни понять причину, ни проверить
  // вывод — а без этого обвинение в подмене ничего не стоит.
  const errorSamples = t(() => {
    const parts: string[] = [];
    const cases: (() => void)[] = [
      () => S.JSON.parse('{'),
      () => new S.Array(-1),
      () => S.decodeURIComponent('%'),
      () => new S.Number(1).toFixed(101),
      () => S.Object.defineProperty(S.Object.freeze({}), 'a', { value: 1 }),
      () => new S.Intl.DateTimeFormat('en', { timeZone: 'Not/AZone' }),
    ];
    for (const c of cases) {
      try {
        c();
        parts.push('no-throw');
      } catch (e: any) {
        parts.push(`${e && e.name}|${e && e.message}`);
      }
    }
    return parts;
  });

  // Форма стек-трейса вынесена в отдельное поле: раньше она входила в общую
  // подпись ошибок, и её расхождение было неотличимо от расхождения текстов.
  const stackShape = t(() => {
    try {
      throw new S.Error('probe');
    } catch (e: any) {
      const stack = String((e && e.stack) || '');
      // Порядок проверок важен. Форму «имя@адрес:строка:столбец» нельзя
      // искать первой по одному лишь символу «@»: он встречается в самих
      // адресах модулей (например, в служебных путях dev-сервера), и тогда
      // главный поток и воркер получают разные подписи на ровном месте.
      // Сначала ищем однозначный маркер V8, и только потом — форму с «@»,
      // причём привязанную к началу строки стека.
      // Неопознанная форма возвращается как null, а не как «other».
      //
      // Разница принципиальная. «other» — это значение, и сравнение
      // контекстов считает его несовпадением: на живом Chrome фрейм с
      // srcdoc дал пустой стек, получил «other» против «at-kw» главного
      // потока, и настоящий браузер был обвинён в переопределённых
      // встроенных функциях. Отсутствие сведений не есть противоречие,
      // и в схеме это выражается через null.
      if (/\n\s+at\s/.test(stack)) return 'at-kw';
      if (/^\s*[^\s@]*@\S+:\d+:\d+/m.test(stack)) return 'at-sign';
      return null;
    }
  });

  const mathSignature = t(() => {
    const M = S.Math;
    const values: number[] = [
      M.acos(0.123456789),
      M.acosh(1e300),
      M.asin(0.123456789),
      M.asinh(1),
      M.atan(2),
      M.atanh(0.5),
      M.cbrt(100),
      M.cos(1e10),
      M.cosh(1),
      M.exp(1),
      M.expm1(1),
      M.log(10),
      M.log1p(10),
      M.sin(1e10),
      M.sinh(1),
      M.tan(-1e300),
      M.tanh(1),
      M.pow(M.PI, -100),
      M.hypot(3, 4, 5),
    ];
    return fnv(values.map((v) => (S.Object.is(v, -0) ? '-0' : String(v))).join(','));
  });

  const resolved = t(() => new S.Intl.DateTimeFormat().resolvedOptions()) as any;

  return {
    userAgent: str(() => nav.userAgent),
    uaDataPlatform: str(() => nav.userAgentData.platform),
    uaDataMobile: bool(() => nav.userAgentData.mobile),
    language: str(() => nav.language),
    languages: t(() => (nav.languages ? Array.from(nav.languages).map(String) : null)),
    hardwareConcurrency: num(() => nav.hardwareConcurrency),
    deviceMemory: num(() => nav.deviceMemory),
    platform: str(() => nav.platform),
    // vendor есть у Navigator, но не у WorkerNavigator — в воркере ждём null.
    vendor: str(() => nav.vendor),
    webdriver: bool(() => nav.webdriver),
    timezone: str(() => resolved.timeZone),
    timezoneOffset: num(() => new S.Date().getTimezoneOffset()),
    // screen и devicePixelRatio в воркерах отсутствуют — тоже null.
    // Размеры экрана снимаются только там, где они означают экран.
    //
    // Во встроенном фрейме с srcdoc WebKit возвращает не размеры дисплея, а
    // величины, выведенные из самого фрейма: на живом Safari главный поток
    // дал 1032×1376, а фрейм — 810×1080. Сравнивать их между собой значит
    // обвинять настоящий браузер за поведение его собственного движка.
    //
    // Во вложенных контекстах поле остаётся пустым, и сравнение их пропустит:
    // отсутствие сведений у нас не улика.
    screenWidth: topLevel() ? num(() => S.screen.width) : null,
    screenHeight: topLevel() ? num(() => S.screen.height) : null,
    colorDepth: num(() => S.screen.colorDepth),
    devicePixelRatio: num(() => S.devicePixelRatio),
    canvasHash: drawCanvas(),
    webglRenderer: webgl ? webgl.renderer : null,
    webglVendor: webgl ? webgl.vendor : null,
    errorSignature: errorSamples ? fnv(errorSamples.join('\n')) : null,
    errorSamples,
    stackShape,
    mathSignature,
    performanceOrigin: num(() => Math.round(S.performance.timeOrigin)),
  };
}

/** Исходник пробы для вставки в Blob воркера. */
export const CONTEXT_PROBE_SOURCE = String(contextProbe);
